import { Switch } from '@src/components/Switch';
import StepProgress from '@src/features/user/checkout/layout/StepProgress';
import { useCheckoutStore } from '@src/stores/checkoutStore';
import { TradingPlatForms } from '@src/types';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@src/features/ui';
import { useNavigate } from 'react-router-dom';
import { useGetMe } from '@src/hooks/useGetMe';

import { accountTypeData, iconsImgPackage, iconsPackage } from './data';

export const SelectAccountTypePageV2 = () => {
  const {
    platform,
    typeIndex,
    setTypeIndex,
    isCompetitionAccount: _isCompetitionAccount,
    setIsCompetitionAccount: _setIsCompetitionAccount,
    isPurchaseMode,
    setPlatform,
  } = useCheckoutStore();

  const { me } = useGetMe();

  const [isCompetitionAccount, setIsCompetitionAccount] = useState(
    _isCompetitionAccount
  );
  const [selectedIdx, setSelectedIdx] = useState<number | undefined>(
    typeIndex || 1
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (isPurchaseMode && !platform && me?.platform) {
      setPlatform(me.platform);
    }

    if (!isPurchaseMode && !platform) {
      navigate('/select-trading-platform');
    }
  }, [navigate, platform, isPurchaseMode, me, setPlatform]);

  const onContinue = () => {
    setTypeIndex(selectedIdx);
    _setIsCompetitionAccount(!!isCompetitionAccount);
    navigate('/select-account');
  };

  const goBack = () => {
    if (isPurchaseMode) {
      navigate('/');
    } else {
      navigate('/select-trading-platform');
    }
    setTypeIndex(undefined);
    _setIsCompetitionAccount(false);
  };

  const totalDueTodayPrice = useMemo(() => {
    if (
      platform === TradingPlatForms.RITHMIC_DATA_FEED &&
      isCompetitionAccount
    ) {
      return 35;
    }

    return 20;
  }, [platform, isCompetitionAccount]);

  const priceStr = useMemo(() => {
    return '$15.00';
  }, [isCompetitionAccount]);

  if (!platform && isPurchaseMode) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!platform) return null;

  return (
    <>
      <div className="">
        <div
          className="max-w-[1440px] m-auto px-5 mt-4 py-4 flex flex-row justify-between gap-x-10"
          style={{
            backgroundColor:
              'rgba(255, 255, 255, 0.05)' /* 5% = 0.05 opacity */,
            backdropFilter: 'blur(200px)',
          }}
        >
          <div className="flex-1">
            <div className="mb-11.5">
              <StepProgress
                accountType={selectedIdx}
                current={isPurchaseMode ? 1 : 2}
                platform={platform}
                skipPlatform={isPurchaseMode}
                total={isPurchaseMode ? 4 : 5}
                onClick={(step) => {
                  console.log(step);
                  if (!isPurchaseMode && !step)
                    navigate('/select-trading-platform');
                }}
              />
            </div>
            <div className="flex flex-col-reverse lg:flex-row gap-[22px]">
              <div className="flex-1">
                <h2 className="font-bold text-[23px] leading-[100%] tracking-normal text-white mb-2">
                  Select Account Type
                </h2>
                <p className="pt-[10px] font-medium text-[16px] leading-[100%] tracking-normal text-white mb-[36px]">
                  Choose the account type based on your preference
                </p>
                <div className="bg-[#25334880] border border-[#33455F] py-5 pb-[30px] px-[30px] space-y-[10px] rounded-[10px]">
                  <div
                    className={
                      'bg-[#171D23] p-5 flex items-center justify-between rounded-[10px]'
                    }
                  >
                    <div className={'space-y-[6px]'}>
                      <p className={'text-white text-sm font-bold'}>
                        Practice Account (qualification path)
                      </p>
                      <div className={'space-y-[16px]'}>
                        <p className={'text-[##CECECE] text-xs font-normal'}>
                          · Standard Payouts (keep 100% of the first $12,500 and
                          then 90% of requested)
                        </p>
                        <p className={'text-[##CECECE] text-xs font-normal'}>
                          · Entry Level Payouts (keep 50% of all payouts
                          requested) (reduce fee by xxx depending on account
                          size)
                        </p>
                      </div>
                    </div>
                    <Button
                      className="w-[135px] font-bold text-[16px]"
                      size="lg"
                      variant={selectedIdx === 1 ? 'gradient' : 'outline'}
                      onClick={() => setSelectedIdx(1)}
                    >
                      {selectedIdx === 1 ? 'Selected' : 'Select'}
                    </Button>
                  </div>

                  <div
                    className={
                      'bg-[#171D23] p-5 flex items-center justify-between rounded-[10px]'
                    }
                  >
                    <div className={'space-y-[6px]'}>
                      <p className={'text-white text-sm font-bold'}>
                        Straight to Performance Account (STPA)
                      </p>
                      <div className={'space-y-[16px]'}>
                        <p className={'text-[##CECECE] text-xs font-normal'}>
                          · Standard Payouts (keep 100% of the first $12,500 and
                          then 90% of requested)
                        </p>
                        <p className={'text-[##CECECE] text-xs font-normal'}>
                          · Entry Level Payouts (keep 50% of all payouts
                          requested) (reduce fee by xxx depending on account
                          size)
                        </p>
                      </div>
                    </div>
                    <Button
                      className="w-[135px] font-bold text-[16px]"
                      size="lg"
                      variant={selectedIdx === 2 ? 'gradient' : 'outline'}
                      onClick={() => setSelectedIdx(2)}
                    >
                      {selectedIdx === 2 ? 'Selected' : 'Select'}
                    </Button>
                  </div>
                  <div
                    className={
                      'bg-[#171D23] p-5 flex items-center justify-between rounded-[10px]'
                    }
                  >
                    <div className={'space-y-[6px]'}>
                      <p className={'text-white text-sm font-bold'}>
                        Specialty Accounts (prebuilt)
                      </p>
                      <div className={'space-y-[16px]'}>
                        <p className={'text-[##CECECE] text-xs font-normal'}>
                          · Turbo (if selected go straight to checkout and pay
                          with detail of account)
                        </p>
                        <p className={'text-[##CECECE] text-xs font-normal'}>
                          · Competition
                        </p>
                      </div>
                    </div>
                    <Button
                      className="w-[135px] font-bold text-[16px]"
                      size="lg"
                      variant={selectedIdx === 3 ? 'gradient' : 'outline'}
                      onClick={() => setSelectedIdx(3)}
                    >
                      {selectedIdx === 3 ? 'Selected' : 'Select'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="relative lg:w-[340px] base:w-full  bg-[#1F2734] rounded-[20px] pt-[30px] pb-[30px] pl-[24px] pr-[24px] flex flex-col gap-[24px]">
                <div className="sticky top-[30px] bg-[#253348] pt-[30px] pb-[30px] pl-[24px] pr-[24px] border border-[#33455F] rounded-[10px] gap-[30px] flex flex-col">
                  <span className="font-bold text-[16px] leading-[100%] tracking-normal text-white">
                    Your Selection
                  </span>
                  <div>
                    <div className="flex items-center gap-[23px] h-[46px]">
                      <svg
                        fill="none"
                        height="30"
                        viewBox="0 0 40 40"
                        width="30"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17.6033 26.5133L28.2633 15.8533L27.0833 14.6733L17.6033 24.1533L12.8533 19.4033L11.6733 20.5833L17.6033 26.5133ZM20.005 35C17.9317 35 15.9817 34.6067 14.155 33.82C12.3294 33.0322 10.7411 31.9633 9.39 30.6133C8.03889 29.2633 6.96944 27.6767 6.18167 25.8533C5.39389 24.03 5 22.0806 5 20.005C5 17.9294 5.39389 15.9794 6.18167 14.155C6.96833 12.3294 8.03556 10.7411 9.38333 9.39C10.7311 8.03889 12.3183 6.96944 14.145 6.18167C15.9717 5.39389 17.9217 5 19.995 5C22.0683 5 24.0183 5.39389 25.845 6.18167C27.6706 6.96833 29.2589 8.03611 30.61 9.385C31.9611 10.7339 33.0306 12.3211 33.8183 14.1467C34.6061 15.9722 35 17.9217 35 19.995C35 22.0683 34.6067 24.0183 33.82 25.845C33.0333 27.6717 31.9644 29.26 30.6133 30.61C29.2622 31.96 27.6756 33.0294 25.8533 33.8183C24.0311 34.6072 22.0817 35.0011 20.005 35Z"
                          fill="#14CA74"
                        />
                      </svg>
                      <div>
                        <div className="text-[12px] text-[#CECECE]">
                          Trading Platform
                        </div>
                        <div className="flex items-center gap-[10px]  mt-[5px]">
                          {
                            iconsImgPackage[
                              platform as keyof typeof iconsImgPackage
                            ].icon
                          }
                          <span className="font-semibold text-[14px] text-white">
                            {
                              iconsImgPackage[
                                platform as keyof typeof iconsImgPackage
                              ].text
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="w-[30px] flex justify-center ">
                      <div className="bg-[#33455F] w-[4px] h-[26px] rounded" />
                    </div>
                    <div className="flex items-center gap-[23px] h-[46px]">
                      <svg
                        className="min-w-[30px]"
                        fill="none"
                        height="30"
                        viewBox="0 0 40 40"
                        width="30"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17.6033 26.5133L28.2633 15.8533L27.0833 14.6733L17.6033 24.1533L12.8533 19.4033L11.6733 20.5833L17.6033 26.5133ZM20.005 35C17.9317 35 15.9817 34.6067 14.155 33.82C12.3294 33.0322 10.7411 31.9633 9.39 30.6133C8.03889 29.2633 6.96944 27.6767 6.18167 25.8533C5.39389 24.03 5 22.0806 5 20.005C5 17.9294 5.39389 15.9794 6.18167 14.155C6.96833 12.3294 8.03556 10.7411 9.38333 9.39C10.7311 8.03889 12.3183 6.96944 14.145 6.18167C15.9717 5.39389 17.9217 5 19.995 5C22.0683 5 24.0183 5.39389 25.845 6.18167C27.6706 6.96833 29.2589 8.03611 30.61 9.385C31.9611 10.7339 33.0306 12.3211 33.8183 14.1467C34.6061 15.9722 35 17.9217 35 19.995C35 22.0683 34.6067 24.0183 33.82 25.845C33.0333 27.6717 31.9644 29.26 30.6133 30.61C29.2622 31.96 27.6756 33.0294 25.8533 33.8183C24.0311 34.6072 22.0817 35.0011 20.005 35Z"
                          fill="#33455F"
                        />
                      </svg>
                      <div>
                        <div className="text-[12px] text-[#CECECE]">
                          Account Type
                        </div>
                        <div className="font-semibold text-[14px]">
                          <span className="text-[14px] font-normal">
                            {accountTypeData[(selectedIdx || 1) - 1].title}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-[20px]">
                      <div className="mt-[30px] h-[3px] bg-[#33455F] w-full" />
                      <div className="flex flex-col gap-[10px]">
                        <div className="flex items-center gap-[23px] justify-between leading-[17px]">
                          <div className="text-[14px] font-medium text-white">
                            {
                              iconsImgPackage[
                                platform as keyof typeof iconsImgPackage
                              ].text
                            }
                          </div>
                          <div className="font-medium text-[14px]">$20.00</div>
                        </div>
                      </div>
                      {platform === TradingPlatForms.RITHMIC_DATA_FEED &&
                        isCompetitionAccount && (
                          <div className="flex items-center gap-[23px] justify-between leading-[17px]">
                            <div className="text-[14px] font-medium text-white">
                              Competition Account
                            </div>
                            <div className="font-medium text-[14px]">
                              $15.00
                            </div>
                          </div>
                        )}
                      <div className="h-[3px] bg-[#33455F] w-full" />
                      <div className="flex items-center gap-[23px] justify-between h-[17px]">
                        <div className="text-[14px] font-bold text-white">
                          Total Due Today
                        </div>
                        <div className="font-bold text-[14px]">
                          ${totalDueTodayPrice.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {platform === TradingPlatForms.RITHMIC_DATA_FEED ? (
                  <div>
                    <span className="text-[18px] font-bold">
                      Only in Rithmic
                    </span>
                    <div className="mt-[10px]">
                      <div className="border border-[#C83837] rounded-[10px] bg-[#253348] pt-[20px] pb-[20px] pl-[18px] pr-[18px] flex flex-col gap-[20px]">
                        <div className="flex items-center justify-between">
                          <span>Competition Account</span>
                          <Switch
                            checked={!!isCompetitionAccount}
                            onChange={() =>
                              setIsCompetitionAccount(!isCompetitionAccount)
                            }
                          />
                        </div>
                        <div className="flex flex-col gap-[12px]">
                          <div className="flex items-center gap-[10px] font-medium">
                            {iconsPackage[0]} Unlimited trades per day
                          </div>
                          <div className="flex items-center gap-[10px] font-medium">
                            {iconsPackage[2]} Advanced risk algorithms
                          </div>
                          <div className="flex items-center gap-[10px] font-medium">
                            {iconsPackage[3]} 24/7 dedicated support
                          </div>
                        </div>
                        <div className="h-[3px] bg-[#33455F] w-full" />
                        <div className="flex items-center gap-[10px] font-medium justify-between">
                          <span>One-Time</span>
                          <span className="font-bold text-[20px]">
                            {priceStr}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full sticky bottom-0 backdrop-blur-[30px] bg-[linear-gradient(90deg,rgba(23,29,35,0.8)_0%,rgba(23,29,35,0.8)_100%)] border-t border-[#255589] h-[100px]">
        <div className="max-w-[1440px] m-auto px-6 lg:px-[60px] flex justify-between items-center h-full">
          <Button className="px-8" size="lg" variant="outline" onClick={goBack}>
            {isPurchaseMode ? 'Cancel' : 'Previous: Select Platform'}
          </Button>
          <Button
            className="px-8"
            disabled={selectedIdx === undefined}
            size="lg"
            variant="gradient"
            onClick={onContinue}
          >
            Next: Select Account Size
          </Button>
        </div>
      </div>
    </>
  );
};
